/**
 * Market-data stream client (SPEC §7.1, §12.0): `wss://…/v1/stream/md`, opened **inside the render
 * worker**. Mirrors `TradingStreamClient` (Hello → Welcome, Ping/Pong, backoff reconnect with a
 * fresh single-use token each attempt) but is worker-safe: no zustand, no DOM, callbacks only.
 *
 * - Subscriptions are ref-counted by requested symbol ("NQ"); the gateway answers each Subscribe
 *   with `InstrumentInfo` (resolved front month, e.g. "NQZ6") echoing the `req_id`.
 *   Unsubscribes go out by resolved contract and only when no other chart still uses it
 *   (`SubscriptionBook`: the gateway keys subscriptions by contract).
 * - Market data is never replayed (§7.1): on reconnect every live subscription is re-sent.
 * - Pongs feed a `ClockOffsetEstimator` for tick-to-pixel (§12.0). No frame within 10 s of a
 *   ping (`Heartbeat`, CB4): the socket is treated as dead (charts show "disconnected" instead of a
 *   frozen last price) and reconnected; `checkLiveness()` probes at once (tab visible again).
 * - History (§6.8): `requestHistory` sends a `HistoryRequest` and routes the `HistoryChunk`s that
 *   echo its `req_id`; a socket drop aborts every pending request (the gateway task dies with it).
 *   A `Notice` echoing a history request's `req_id` (`TOO_MANY_REQUESTS`,
 *   `HISTORY_RANGE_TOO_LARGE`, …) goes to that request's handlers, never to `onNotice`.
 * - A subscribe refused with `TOO_MANY_REQUESTS` (socket busy) is re-sent up to
 *   `SUBSCRIBE_RETRIES` times (1, 2, 4 s) while the symbol is still wanted; other notices
 *   (`MD_SYMBOL_LIMIT`, `SUBSCRIBE_FAILED`) are final until the next reconnect.
 * - Each connect attempt carries a generation number, so a `stop()`/`start()` while a token is
 *   being fetched, or a late `close` event from a replaced socket, can never open a second socket
 *   or clear the new socket's timers.
 */
import { create, fromBinary, toBinary } from "@bufbuild/protobuf";
import { EnvelopeSchema, type Envelope } from "../gen/helm/v1/envelope_pb";
import { HelloSchema, PingSchema, type Notice } from "../gen/helm/v1/session_pb";
import {
  BarSpecSchema,
  HistoryRequestSchema,
  SubscribeSchema,
  UnsubscribeSchema,
  type Bbo,
  type BookUpdate,
  type HistoryChunk,
  type InstrumentInfo,
  type MarketDataGap,
  type TradeBatch,
} from "../gen/helm/v1/market_data_pb";
import { ReconnectPolicy } from "./backoff";
import { ClockOffsetEstimator } from "./clockOffset";
import { Heartbeat, PING_INTERVAL_MS } from "./heartbeat";
import { NOTICE_TOO_MANY_REQUESTS } from "./notices";
import { SubscriptionBook } from "./subscriptions";

export * from "./notices";
export type { Bbo, BookUpdate, HistoryChunk, InstrumentInfo, MarketDataGap, Notice, TradeBatch };

export type HistoryRequestInit = {
  symbol: string;
  spec: { kind: number; period: number };
  fromNs: bigint;
  toNs: bigint;
  forceReload: boolean;
};

export type HistoryHandlers = {
  onChunk: (chunk: HistoryChunk) => void;
  /** The socket closed (or the client stopped) before the final chunk. */
  onAbort: (reason: string) => void;
  /** A gateway notice answering this request; the request's (empty) final chunk follows. */
  onNotice?: (notice: Notice) => void;
};

export type MdStatus = "connecting" | "connected" | "disconnected";

export type MdClientOptions = {
  baseUrl: string;
  getToken: () => Promise<string>;
  clientVersion: string;
  tabId: string;
  onStatus?: (status: MdStatus, detail: string | null) => void;
  /** `requested` is what the caller subscribed with ("NQ"); `info.symbol` is the resolved contract. */
  onInstrument?: (requested: string, info: InstrumentInfo) => void;
  /** Last-only metadata: current reference lifetime and socket, separate from legacy bar resolution. */
  onLastInstrument?: (requested: string, info: InstrumentInfo) => void;
  /** `recvMs` is the browser wall-clock receive time (epoch ms, sub-ms precision). */
  onTrades?: (batch: TradeBatch, recvMs: number) => void;
  onBbo?: (bbo: Bbo) => void;
  /** Top-N order book (gateway `BookUpdate`, 10-level MBP until MBO; chart heatmap, SPEC §12.8). */
  onBook?: (book: BookUpdate) => void;
  onNotice?: (notice: Notice, requested: string | null) => void;
  /** The ticker plant reconnected: `gap.symbol`'s live data may be missing for the window (CB3). */
  onGap?: (gap: MarketDataGap) => void;
  /**
   * Every `Welcome` (each (re)connect), before the status turns `connected`. `marketDataSource`
   * names where the gateway's market data comes from ("mock", "Rithmic Test@host"; ADR-0028).
   */
  onWelcome?: (welcome: { marketDataSource: string; sessionId: string }) => void;
  pingIntervalMs?: number;
  /** No frame this long after a ping: the socket is dropped (default 10 s). */
  pongTimeoutMs?: number;
};

/** Extra pings right after connect so the clock offset converges before the first paint. */
const PING_BURST_MS = [250, 750] as const;
/** Re-sends of a subscribe the gateway refused as `TOO_MANY_REQUESTS` (1 s, 2 s, 4 s apart). */
export const SUBSCRIBE_RETRIES = 3;

/** Browser wall clock with sub-millisecond precision (works in workers). */
export function wallNowMs(): number {
  return performance.timeOrigin + performance.now();
}

export class MdStreamClient {
  readonly clock = new ClockOffsetEstimator();
  private ws: WebSocket | null = null;
  private stopped = true;
  /** Bumped by start/stop; a connect attempt from an older generation gives up. */
  private generation = 0;
  private readonly policy = new ReconnectPolicy();
  private reqCounter = 0;
  private readonly heartbeat: Heartbeat;
  private readonly burstTimers = new Set<ReturnType<typeof setTimeout>>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly retryTimers = new Set<ReturnType<typeof setTimeout>>();
  /** Busy-refusal retries per requested symbol on the current socket. */
  private readonly subRetries = new Map<string, number>();
  private readonly subs = new SubscriptionBook();
  private readonly resolved = new Map<string, InstrumentInfo>();
  private readonly pendingSubs = new Map<string, string>();
  /** Display authority must not follow a retired request/retry into a same-symbol resubscription. */
  private readonly lastLifetimes = new Map<string, symbol>();
  private readonly lastPending = new Map<string, symbol>();
  private readonly lastMetadata = new Map<string, InstrumentInfo>();
  private readonly pendingPings = new Map<string, number>();
  private readonly history = new Map<string, HistoryHandlers>();
  private readonly opts: MdClientOptions;
  status: MdStatus = "disconnected";

  constructor(opts: MdClientOptions) {
    this.opts = opts;
    this.heartbeat = new Heartbeat({
      sendPing: () => this.sendPing(),
      onDead: (reason) => {
        this.dropSocket(reason);
      },
      intervalMs: opts.pingIntervalMs ?? PING_INTERVAL_MS,
      ...(opts.pongTimeoutMs !== undefined ? { timeoutMs: opts.pongTimeoutMs } : {}),
    });
  }

  /** The page is visible again (CB4): probe the socket now, or reconnect now when there is none. */
  checkLiveness(): void {
    if (this.stopped) return;
    if (this.status === "connected" && this.heartbeat.isRunning) {
      this.heartbeat.probe();
      return;
    }
    if (this.reconnectTimer === null) return; // a connect attempt is in progress
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    void this.connect(this.generation);
  }

  /** Missed heartbeat: the socket may never fire `close`; give up on it and reconnect. */
  private dropSocket(reason: string): void {
    const ws = this.ws;
    if (ws === null) return;
    this.ws = null;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      ws.close(4000, "heartbeat timeout");
    } catch {
      /* already closing */
    }
    this.clearTimers();
    this.onSocketGone("disconnected");
    this.setStatus("disconnected", reason);
    this.scheduleReconnect();
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect(++this.generation);
  }

  stop(): void {
    this.stopped = true;
    this.generation++;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000, "client stop");
    this.onSocketGone("client stopped");
    this.setStatus("disconnected", null);
  }

  /** Reference-counted subscribe by requested symbol. */
  subscribe(symbol: string): void {
    const first = this.subs.add(symbol);
    if (first) this.lastLifetimes.set(symbol, Symbol());
    if (first && this.status === "connected") this.sendSubscribe(symbol, this.lastLifetimes.get(symbol));
    const info = this.resolved.get(symbol);
    if (!first && info) this.opts.onInstrument?.(symbol, info);
    const last = this.lastMetadata.get(symbol);
    if (!first && last && this.status === "connected") this.opts.onLastInstrument?.(symbol, last);
  }

  unsubscribe(symbol: string): void {
    const contract = this.subs.remove(symbol);
    if (!this.subs.isReferenced(symbol)) {
      this.resolved.delete(symbol);
      this.lastLifetimes.delete(symbol);
      this.lastMetadata.delete(symbol);
    }
    if (contract !== null && this.status === "connected") this.sendUnsubscribe(contract);
  }

  /**
   * Send a `HistoryRequest` (§6.8). Returns a cancel function (stops routing chunks; the gateway
   * has no cancel message), or null when the socket is not connected.
   */
  requestHistory(init: HistoryRequestInit, handlers: HistoryHandlers): (() => void) | null {
    if (this.status !== "connected" || this.ws?.readyState !== WebSocket.OPEN) return null;
    const reqId = this.send({
      case: "historyReq",
      value: create(HistoryRequestSchema, {
        symbol: init.symbol,
        spec: create(BarSpecSchema, { kind: init.spec.kind, period: init.spec.period }),
        fromNs: init.fromNs,
        toNs: init.toNs,
        forceReload: init.forceReload,
      }),
    });
    this.history.set(reqId, handlers);
    return () => {
      this.history.delete(reqId);
    };
  }

  /** History requests awaiting their final chunk. */
  get pendingHistory(): number {
    return this.history.size;
  }

  instrument(requested: string): InstrumentInfo | undefined {
    return this.resolved.get(requested);
  }

  private setStatus(s: MdStatus, detail: string | null): void {
    this.status = s;
    this.opts.onStatus?.(s, detail);
  }

  private send(body: Envelope["body"]): string {
    const reqId = `m${String(++this.reqCounter)}`;
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(toBinary(EnvelopeSchema, create(EnvelopeSchema, { seq: 0n, reqId, body })));
    }
    return reqId;
  }

  private sendUnsubscribe(contract: string): void {
    this.send({ case: "unsubscribe", value: create(UnsubscribeSchema, { symbol: contract }) });
  }

  private sendSubscribe(symbol: string, lastLifetime: symbol | undefined): void {
    const reqId = this.send({
      case: "subscribe",
      value: create(SubscribeSchema, { symbol, wantTrades: true, wantBbo: true, wantBook: true }),
    });
    this.pendingSubs.set(reqId, symbol);
    if (lastLifetime !== undefined) this.lastPending.set(reqId, lastLifetime);
  }

  private current(gen: number): boolean {
    return !this.stopped && gen === this.generation;
  }

  /** Socket closed or client stopped: pending work tied to the socket is void. */
  private onSocketGone(reason: string): void {
    for (const t of this.retryTimers) clearTimeout(t);
    this.retryTimers.clear();
    this.subRetries.clear();
    this.pendingSubs.clear();
    this.lastPending.clear();
    this.lastMetadata.clear();
    this.pendingPings.clear();
    this.subs.clearResolutions();
    this.resolved.clear();
    const pending = [...this.history.values()];
    this.history.clear();
    for (const h of pending) h.onAbort(reason);
  }

  private async connect(gen: number): Promise<void> {
    if (!this.current(gen)) return;
    this.setStatus("connecting", null);
    let token: string;
    try {
      token = await this.opts.getToken();
    } catch (e) {
      if (!this.current(gen)) return;
      this.setStatus("disconnected", e instanceof Error ? e.message : "token fetch failed");
      this.scheduleReconnect();
      return;
    }
    if (!this.current(gen)) return;

    let ws: WebSocket;
    try {
      ws = new WebSocket(`${this.opts.baseUrl}/v1/stream/md`);
    } catch (e) {
      // A synchronous throw (bad URL, blocked port) would otherwise end reconnects for good.
      this.setStatus("disconnected", e instanceof Error ? e.message : "socket failed");
      this.scheduleReconnect();
      return;
    }
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onopen = () => {
      const hello = create(HelloSchema, {
        jwt: token,
        clientVersion: this.opts.clientVersion,
        tabId: this.opts.tabId,
      });
      ws.send(
        toBinary(
          EnvelopeSchema,
          create(EnvelopeSchema, { seq: 0n, reqId: "", body: { case: "hello", value: hello } }),
        ),
      );
    };
    ws.onmessage = (ev: MessageEvent<ArrayBuffer | string>) => {
      const recvMs = wallNowMs();
      if (this.ws !== ws) return;
      this.heartbeat.alive();
      if (!(ev.data instanceof ArrayBuffer)) return;
      let env: Envelope;
      try {
        env = fromBinary(EnvelopeSchema, new Uint8Array(ev.data));
      } catch {
        return; // a malformed frame must not kill the worker's message loop
      }
      this.handle(env, recvMs);
    };
    ws.onclose = (ev) => {
      // A socket replaced by stop()/start() must not touch the new one's state.
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearTimers();
      this.onSocketGone("disconnected");
      this.setStatus("disconnected", ev.reason || `closed ${String(ev.code)}`);
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      // onclose follows.
    };
  }

  private handle(env: Envelope, recvMs: number): void {
    switch (env.body.case) {
      case "trades":
        this.opts.onTrades?.(env.body.value, recvMs);
        break;
      case "bbo":
        this.opts.onBbo?.(env.body.value);
        break;
      case "book":
        this.opts.onBook?.(env.body.value);
        break;
      case "welcome":
        this.lastPending.clear();
        this.lastMetadata.clear();
        this.opts.onWelcome?.({
          marketDataSource: env.body.value.marketDataSource,
          sessionId: env.body.value.sessionId,
        });
        this.policy.connected(wallNowMs());
        this.setStatus("connected", null);
        for (const sym of this.subs.requested()) this.sendSubscribe(sym, this.lastLifetimes.get(sym));
        this.startPings();
        break;
      case "instrument": {
        const lastLifetime = this.lastPending.get(env.reqId);
        this.lastPending.delete(env.reqId);
        const requested = this.pendingSubs.get(env.reqId);
        this.pendingSubs.delete(env.reqId);
        if (requested === undefined) break;
        const r = this.subs.resolve(requested, env.body.value.symbol);
        if (r.unsubscribe !== null) this.sendUnsubscribe(r.unsubscribe);
        if (!r.accept) break;
        this.resolved.set(requested, env.body.value);
        this.opts.onInstrument?.(requested, env.body.value);
        if (
          lastLifetime !== undefined &&
          lastLifetime === this.lastLifetimes.get(requested) &&
          this.status === "connected"
        ) {
          this.lastMetadata.set(requested, env.body.value);
          this.opts.onLastInstrument?.(requested, env.body.value);
        }
        break;
      }
      case "historyChunk": {
        const h = this.history.get(env.reqId);
        if (!h) break;
        if (env.body.value.final) this.history.delete(env.reqId);
        h.onChunk(env.body.value);
        break;
      }
      case "notice": {
        const h = this.history.get(env.reqId);
        if (h) {
          h.onNotice?.(env.body.value);
          break;
        }
        const requested = this.pendingSubs.get(env.reqId) ?? null;
        if (requested !== null) {
          const lastLifetime = this.lastPending.get(env.reqId);
          this.lastPending.delete(env.reqId);
          this.pendingSubs.delete(env.reqId);
          if (env.body.value.code === NOTICE_TOO_MANY_REQUESTS) this.retrySubscribe(requested, lastLifetime);
        }
        this.opts.onNotice?.(env.body.value, requested);
        break;
      }
      case "mdGap":
        this.opts.onGap?.(env.body.value);
        break;
      case "pong": {
        const t0 = this.pendingPings.get(env.reqId);
        if (t0 !== undefined) {
          this.pendingPings.delete(env.reqId);
          this.clock.add({ t0, t1: Number(env.body.value.tGwMs), t3: recvMs });
        }
        break;
      }
      default:
        break;
    }
  }

  /** Re-send a busy-refused subscribe after 1, 2, 4 s while the symbol is still wanted. */
  private retrySubscribe(requested: string, lastLifetime: symbol | undefined): void {
    const n = this.subRetries.get(requested) ?? 0;
    if (n >= SUBSCRIBE_RETRIES) return;
    this.subRetries.set(requested, n + 1);
    const t = setTimeout(
      () => {
        this.retryTimers.delete(t);
        if (this.status !== "connected" || !this.subs.isReferenced(requested) || this.resolved.has(requested))
          return;
        this.sendSubscribe(requested, lastLifetime);
      },
      1000 * 2 ** n,
    );
    this.retryTimers.add(t);
  }

  /** One ping (heartbeat and clock offset); false when the socket is not open. */
  private sendPing(): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    const t0 = wallNowMs();
    const reqId = this.send({
      case: "ping",
      value: create(PingSchema, { tClientMs: BigInt(Math.round(t0)) }),
    });
    this.pendingPings.set(reqId, t0);
    if (this.pendingPings.size > 10) {
      const oldest = this.pendingPings.keys().next().value;
      if (oldest !== undefined) this.pendingPings.delete(oldest);
    }
    return true;
  }

  private startPings(): void {
    this.stopPings();
    // The heartbeat pings now and every interval; a short burst right after connect makes the
    // clock offset converge before the first paint.
    this.heartbeat.start();
    for (const ms of PING_BURST_MS) {
      const t = setTimeout(() => {
        this.burstTimers.delete(t);
        this.sendPing();
      }, ms);
      this.burstTimers.add(t);
    }
  }

  private stopPings(): void {
    this.heartbeat.stop();
    for (const t of this.burstTimers) clearTimeout(t);
    this.burstTimers.clear();
  }

  private clearTimers(): void {
    this.stopPings();
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer !== null) return;
    const gen = this.generation;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect(gen);
    }, this.policy.nextDelayMs(wallNowMs()));
  }
}
